import { Paperclip, RotateCw, Send, Trash, Upload } from "lucide-react";
import PropTypes from "prop-types";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { removeSessionModel } from "../../../../abstract/controller/utils/historyUtils";
import styles from "./ChatInput.module.scss";

const ChatInput = forwardRef(
  (
    {
      message,
      setMessage,
      onSend,
      onFileClick,
      onDuplicateLastMessage,
      isDragging,
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop,
      hasMessages,
      attachedFilesCount,
      setMessages,
      clearAllStorage,
    },
    ref
  ) => {
    const textareaRef = useRef(null);

    const adjustTextareaHeight = useCallback(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
      }
    }, []);

    const handleClearAllMessages = useCallback(
      (warning) => {
        if (warning && !window.confirm("Are you sure you want to delete all messages?")) {
          return;
        }
        
        setMessages([]);
        clearAllStorage();
        removeSessionModel();
      },
      [clearAllStorage, setMessages]
    );

    useEffect(() => {
      adjustTextareaHeight();
    }, [message, adjustTextareaHeight]);

    useEffect(() => {
      const handleClearStorage = () => {
        handleClearAllMessages(false);
      };

      window.addEventListener("clearStorage", handleClearStorage);

      return () => {
        window.removeEventListener("clearStorage", handleClearStorage);
      };
    }, [handleClearAllMessages]);

    const handleKeyPress = useCallback(
      (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onSend();
        }
      },
      [onSend]
    );

    const resetTextareaHeight = useCallback(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }, []);

    useImperativeHandle(ref, () => ({
      resetHeight: resetTextareaHeight,
      focus: () => textareaRef.current?.focus(),
    }));

    return (
      <div
        className={`${styles.chatInput} ${isDragging ? styles.chatInputDragActive : ""}`}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}>
        {isDragging && (
          <div className={styles.dropOverlay}>
            <Upload size={32} className={styles.dropOverlayIcon} />
            <span className={styles.dropOverlayText}>Drop files to upload</span>
          </div>
        )}

        <div className={styles.textareaWrapper}>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type a message..."
            className={styles.chatTextarea}
            rows={1}
          />

          <div className={styles.buttonContainer}>
            {hasMessages && (
              <button
                onClick={onDuplicateLastMessage}
                className={styles.actionButton}
                title="Duplicate last message">
                <RotateCw size={17} />
              </button>
            )}

            {hasMessages && (
              <button
                onClick={handleClearAllMessages}
                className={styles.actionButton}
                title="Clear all chat history">
                <Trash size={17} />
              </button>
            )}

            <button onClick={onFileClick} className={styles.fileUploadButton} title="Attach files">
              <Paperclip size={17} />
            </button>

            <button
              onClick={onSend}
              disabled={!message.trim() && attachedFilesCount === 0}
              className={`${styles.sendButton} ${!message.trim() && attachedFilesCount === 0 ? styles.sendButtonDisabled : ""}`}
              title="Send message">
              <Send size={17} />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

ChatInput.propTypes = {
  message: PropTypes.string.isRequired,
  setMessage: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onFileClick: PropTypes.func.isRequired,
  onDuplicateLastMessage: PropTypes.func.isRequired,
  onClearAllMessages: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  onDragEnter: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  hasMessages: PropTypes.bool.isRequired,
  attachedFilesCount: PropTypes.number.isRequired,
};

export default ChatInput;
